/**
 * The active workspace and what the signed-in user may do inside it.
 *
 * The permission array here is the *same array* the API refuses on — it comes
 * straight off `TenantMembershipSummary.permissions`, which the backend built
 * with `resolveEffectivePermissions`. Nothing in the UI re-derives it from the
 * role, which is what keeps a hidden control and a 403 from ever disagreeing.
 *
 * That also means this is a convenience, not a boundary: hiding a button stops
 * nobody, and every one of these permissions is checked again on the server.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { hasPermission } from '@app/common';
import type { Permission, TenantMembershipSummary } from '@app/common';
import { listMyTenants } from '../api/tenants.api';
import { useAuth } from './AuthContext';

/** Survives a reload, so a refresh does not silently move the user to another workspace. */
const ACTIVE_TENANT_KEY = 'activeTenantId';

interface TenantContextValue {
  memberships: TenantMembershipSummary[];
  active: TenantMembershipSummary | null;
  activeTenantId: string | null;
  loading: boolean;
  error: string | null;
  setActiveTenantId: (tenantId: string) => void;
  /** True only when the active workspace grants it. False while still loading. */
  can: (permission: Permission) => boolean;
  refresh: () => void;
}

const TenantStateContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<TenantMembershipSummary[]>([]);
  const [activeTenantId, setActiveId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_TENANT_KEY)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!user) {
      setMemberships([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const data = await listMyTenants();
        if (cancelled) return;
        setMemberships(data);
      } catch (err: unknown) {
        if (cancelled) return;
        setError((err as { message?: string }).message ?? 'Failed to load workspaces');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, nonce]);

  // A stored id can name a workspace the user was just removed from, so the
  // stored value is a preference and the roster is the authority.
  const active = useMemo(() => {
    const stored = memberships.find((m) => m.tenant.id === activeTenantId);
    return stored ?? memberships[0] ?? null;
  }, [memberships, activeTenantId]);

  const setActiveTenantId = useCallback((tenantId: string) => {
    localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    setActiveId(tenantId);
  }, []);

  const can = useCallback(
    (permission: Permission) => (active ? hasPermission(active.permissions, permission) : false),
    [active]
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      memberships,
      active,
      activeTenantId: active?.tenant.id ?? null,
      loading,
      error,
      setActiveTenantId,
      can,
      refresh: () => setNonce((n) => n + 1),
    }),
    [memberships, active, loading, error, setActiveTenantId, can]
  );

  return <TenantStateContext.Provider value={value}>{children}</TenantStateContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantStateContext);
  if (!context) throw new Error('useTenant must be used inside a TenantProvider');
  return context;
}

/**
 * Whether the current user may do one thing in the active workspace.
 *
 * A hook rather than a bare call so a component that gates on a permission
 * re-renders when the workspace changes — switching workspaces changes the
 * answer, and a value read once at mount would keep showing the old one.
 */
export function usePermission(permission: Permission): boolean {
  const { can } = useTenant();
  return can(permission);
}
